import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileSignature, CheckCircle, Clock, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function DriverContracts() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`${API_URL}/contracts`);
      setContracts(response.data);
    } catch (error) {
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'signed') {
      return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Signed</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pending Signature</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="driver-contracts">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Contracts</h1>
          <p className="text-muted-foreground mt-1">View and sign your service agreements</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading contracts...</div>
        ) : contracts.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <FileSignature className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
              <h3 className="text-lg font-medium mb-2">No Contracts</h3>
              <p className="text-muted-foreground">You don't have any contracts yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {contracts.map((contract) => (
              <Card key={contract.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        contract.status === 'signed' ? 'bg-green-100' : 'bg-amber-100'
                      }`}>
                        {contract.status === 'signed' ? (
                          <CheckCircle className="h-6 w-6 text-green-600" />
                        ) : (
                          <Clock className="h-6 w-6 text-amber-600" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{contract.contract_type}</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Created: {new Date(contract.created_at).toLocaleDateString()}
                        </p>
                        {contract.signed_at && (
                          <p className="text-sm text-green-600 mt-1">
                            Signed: {new Date(contract.signed_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(contract.status)}
                      {contract.status === 'pending' ? (
                        <Link to={`/driver/contracts/${contract.id}/sign`}>
                          <Button data-testid={`sign-contract-${contract.id}`}>
                            <FileSignature className="h-4 w-4 mr-2" />
                            Sign Contract
                          </Button>
                        </Link>
                      ) : (
                        <Link to={`/driver/contracts/${contract.id}/sign`}>
                          <Button variant="outline" data-testid={`view-contract-${contract.id}`}>
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
